// The API always returns office hours Monday to Sunday.
export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const DAY_PREFIXES = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

const dayIndexOf = (day, position) => {
  if (typeof day === 'string') {
    const index = DAY_PREFIXES.indexOf(day.slice(0, 3).toLowerCase())
    if (index !== -1) return index
  }
  return position
}

const parseTime = value => {
  const match = /^(\d{1,2}):(\d{2})/.exec(value || '')
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 24 || minutes > 59) return null
  return hours * 60 + minutes
}

/**
 * Formats a 24-hour "HH:MM" string as 12-hour time, e.g. "16:00" -> "4:00 PM".
 * Returns null when the value cannot be parsed.
 */
export const formatTime = value => {
  const total = parseTime(value)
  if (total == null) return null
  const hours = Math.floor(total / 60) % 24
  const minutes = String(total % 60).padStart(2, '0')
  const period = hours < 12 ? 'AM' : 'PM'
  return `${hours % 12 || 12}:${minutes} ${period}`
}

/**
 * Normalizes raw officeHours into 7 day entries ordered Monday to Sunday,
 * each with a display label ("9:00 AM – 4:00 PM" or "Closed").
 * Returns an empty array when hours are missing or unusable.
 */
export const normalizeOfficeHours = officeHours => {
  if (!Array.isArray(officeHours)) return []
  const days = []
  officeHours.forEach((entry, position) => {
    if (!entry) return
    const index = dayIndexOf(entry.day, position)
    if (index < 0 || index > 6 || days[index]) return
    const open = formatTime(entry.open)
    const close = formatTime(entry.close)
    const closed = entry.closed === true || !open || !close
    days[index] = {
      index,
      day: DAY_LABELS[index],
      closed,
      open: closed ? null : parseTime(entry.open),
      close: closed ? null : parseTime(entry.close),
      label: closed ? 'Closed' : `${open} – ${close}`,
    }
  })
  return days.filter(Boolean)
}

/**
 * Collapses consecutive days with identical hours,
 * e.g. [{ days: 'Mon–Thu', label: '9:00 AM – 4:00 PM' }, { days: 'Fri–Sun', label: 'Closed' }].
 */
export const groupOfficeHours = officeHours => {
  const groups = []
  normalizeOfficeHours(officeHours).forEach(day => {
    const last = groups[groups.length - 1]
    if (last && last.label === day.label && last.endIndex === day.index - 1) {
      last.endIndex = day.index
      last.end = day.day
    } else {
      groups.push({
        label: day.label,
        start: day.day,
        end: day.day,
        endIndex: day.index,
      })
    }
  })
  return groups.map(({ label, start, end }) => ({
    days: start === end ? start : `${start}–${end}`,
    label,
  }))
}

/**
 * Returns the weekday index (0 = Monday) and minutes since midnight
 * for `now` in the given IANA timezone, or null if the timezone is invalid.
 */
const zonedNow = (timezone, now) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now)
    const get = type => parts.find(part => part.type === type)?.value
    const index = DAY_PREFIXES.indexOf(get('weekday')?.toLowerCase())
    if (index === -1) return null
    return {
      index,
      minutes: (Number(get('hour')) % 24) * 60 + Number(get('minute')),
    }
  } catch {
    return null
  }
}

/**
 * Whether the location is open at `now` in its own timezone.
 * Returns null when it cannot be determined (no hours or no timezone).
 */
export const isOpenAt = (officeHours, timezone, now = new Date()) => {
  if (!timezone) return null
  const days = normalizeOfficeHours(officeHours)
  if (days.length !== 7) return null
  const current = zonedNow(timezone, now)
  if (!current) return null
  const today = days[current.index]
  if (today.closed) return false
  // Hours that close past midnight (e.g. 18:00 – 02:00) are not expected
  // from the dashboard, so a close before open is treated as closed.
  return current.minutes >= today.open && current.minutes < today.close
}
